import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState, useMemo } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { X } from 'src/components/common/icons';
import { Field, SelectField } from './Master';
import { ITableConfig, ITableColumn, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Pager from 'src/components/common/table/Pager';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

const PAGE_SIZE = 25;

interface IClerk {
    county: string;
    addr: string | null;
    city: string | null;
    state: string;
    zip: string | null;
    phone: string | null;
    id?: string;
}

interface INotary {
    name1: string;
    commissionDate: string | null;
    phone: string | null;
    id?: string;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

type TabType = 'clerks' | 'notary';
type ViewMode = 'list' | 'form';

const ClerksNotaryModal: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, post, put, del, response, loading } = useFetch();

    const [activeTab, setActiveTab] = useState<TabType>('clerks');
    const [viewMode, setViewMode] = useState<ViewMode>('list');

    // --- Paginated Clerks State ---
    const [clerksData, setClerksData] = useState<PagedResultSet<IClerk> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [stateFilter, setStateFilter] = useState('');
    const [countyFilter, setCountyFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [shouldReloadClerks, setShouldReloadClerks] = useState(true);

    // --- Local Notaries State ---
    const [notaries, setNotaries] = useState<INotary[]>([]);
    const [notarySearchQuery, setNotarySearchQuery] = useState('');

    // --- Form States ---
    const [editingClerk, setEditingClerk] = useState<IClerk | null>(null);
    const [editingNotary, setEditingNotary] = useState<INotary | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    // --- Lookup States ---
    const [statesList, setStatesList] = useState<{ value: string; label: string }[]>([]);
    const [countiesList, setCountiesList] = useState<{ value: string; label: string }[]>([]); // For the Form
    const [filterCountiesList, setFilterCountiesList] = useState<{ value: string; label: string }[]>([]); // For the Toolbar

    // Load States on mount
    useEffect(() => {
        let isMounted = true;
        const loadStates = async (): Promise<void> => {
            const res = await get('/v1.0/leases/states');
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setStatesList(res.map((s: { stCode: string; abbr: string }) => ({ value: s.stCode, label: s.abbr })));
                }
            }
        };

        if (isOpen) {
            loadStates();
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Load Counties dynamically when a state is selected in the Clerk FORM
    useEffect(() => {
        let isMounted = true;
        const loadCounties = async (stateCode: string): Promise<void> => {
            const res = await get(`/v1.0/leases/states/${stateCode}/counties`);
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setCountiesList(res.map((r: { county: string }) => ({ value: r.county, label: r.county })));
                } else {
                    setCountiesList([]);
                }
            }
        };

        if (viewMode === 'form' && activeTab === 'clerks' && editingClerk && editingClerk.state) {
            loadCounties(editingClerk.state);
        } else {
            setCountiesList([]);
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, activeTab, editingClerk?.state]);

    // Load Counties dynamically when a state is selected in the FILTER Toolbar
    useEffect(() => {
        let isMounted = true;
        const loadFilterCounties = async (stateCode: string): Promise<void> => {
            const res = await get(`/v1.0/leases/states/${stateCode}/counties`);
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setFilterCountiesList(res.map((r: { county: string }) => ({ value: r.county, label: r.county })));
                } else {
                    setFilterCountiesList([]);
                }
            }
        };

        if (viewMode === 'list' && activeTab === 'clerks' && stateFilter) {
            loadFilterCounties(stateFilter);
        } else {
            setFilterCountiesList([]);
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, activeTab, stateFilter]);

    useEffect(() => {
        let isMounted = true;
        const loadNotaries = async (): Promise<void> => {
            const res = await get('/v1.0/leases/admin/notaries');
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setNotaries(res);
                }
            }
        };

        if (isOpen && activeTab === 'notary') {
            loadNotaries();
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeTab]);

    useEffect(() => {
        const loadClerks = async (): Promise<void> => {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();

            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (stateFilter.trim()) {
                params.append('state', stateFilter.trim());
            }
            if (countyFilter.trim()) {
                params.append('county', countyFilter.trim());
            }
            if (searchFilter.trim()) {
                params.append('search', searchFilter.trim());
            }

            const data = await get(`/v1.0/leases/admin/clerks?${params.toString()}`);

            if (data && typeof data.totalCount !== 'undefined') {
                const pagedResult = new PagedResultSet<IClerk>();
                pagedResult.items = data.items.map((c: IClerk) => ({ ...c, id: `${c.state}-${c.county}` }));
                pagedResult.totalRecords = data.totalCount;
                pagedResult.pageNumber = pageNumber;
                pagedResult.pageSize = PAGE_SIZE;

                setClerksData(pagedResult);
            }
        };

        if (isOpen && activeTab === 'clerks' && shouldReloadClerks) {
            loadClerks();
            setShouldReloadClerks(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeTab, shouldReloadClerks, pageNumber]);

    const resetToSearch = (): void => {
        setViewMode('list');
        setEditingClerk(null);
        setEditingNotary(null);
        setIsCreating(false);

        if (activeTab === 'clerks') {
            setShouldReloadClerks(true);
        }
    };

    const handleClerkSearch = (): void => {
        setPageNumber(1);
        setShouldReloadClerks(true);
    };

    const handleClerkClearFilters = (): void => {
        setStateFilter('');
        setCountyFilter('');
        setSearchFilter('');
        setPageNumber(1);
        setShouldReloadClerks(true);
    };

    const handleClerkPageChange = (newPageNumber: number): void => {
        setPageNumber(newPageNumber);
        setShouldReloadClerks(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            handleClerkSearch();
        }
    };

    const handleAddClick = (): void => {
        setIsCreating(true);
        if (activeTab === 'clerks') {
            setEditingClerk({ county: '', state: '', addr: '', city: '', zip: '', phone: '' });
        } else {
            setEditingNotary({ name1: '', commissionDate: null, phone: '' });
        }
        setViewMode('form');
    };

    const handleClerkRowClick = (id: string | number): void => {
        if (clerksData && clerksData.items) {
            const selected = clerksData.items.find((c) => c.id === id);
            if (selected) {
                setIsCreating(false);
                setEditingClerk(selected);
                setViewMode('form');
            }
        }
    };

    const handleNotaryRowClick = (id: string | number): void => {
        const selected = notaries.find((n) => n.name1 === id);
        if (selected) {
            setIsCreating(false);
            setEditingNotary(selected);
            setViewMode('form');
        }
    };

    const handleSave = async (): Promise<void> => {
        setRequestError(null);
        if (activeTab === 'clerks' && editingClerk) {
            if (!editingClerk.county || !editingClerk.state) {
                alert('County and State are required fields.');
                return;
            }
            if (isCreating) {
                await post('/v1.0/leases/admin/clerks', editingClerk);
            } else {
                await put('/v1.0/leases/admin/clerks', editingClerk);
            }

            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save Clerk record.' }, response.status));
            }
        } else if (activeTab === 'notary' && editingNotary) {
            if (!editingNotary.name1) {
                alert('Name is a required field.');
                return;
            }
            if (isCreating) {
                await post('/v1.0/leases/admin/notaries', editingNotary);
            } else {
                await put('/v1.0/leases/admin/notaries', editingNotary);
            }

            if (response.ok) {
                const res = await get('/v1.0/leases/admin/notaries');
                if (res && Array.isArray(res)) {
                    setNotaries(res);
                }
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save Notary record.' }, response.status));
            }
        }
    };

    const handleDelete = async (): Promise<void> => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        if (activeTab === 'clerks' && editingClerk) {
            setRequestError(null);
            await del(`/v1.0/leases/admin/clerks?county=${encodeURIComponent(editingClerk.county)}&state=${encodeURIComponent(editingClerk.state)}`);
            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to delete Clerk record.' }, response.status));
            }
        } else if (activeTab === 'notary' && editingNotary) {
            setRequestError(null);
            await del(`/v1.0/leases/admin/notaries?name=${encodeURIComponent(editingNotary.name1)}`);
            if (response.ok) {
                const res = await get('/v1.0/leases/admin/notaries');
                if (res && Array.isArray(res)) {
                    setNotaries(res);
                }
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to delete Notary record.' }, response.status));
            }
        }
    };

    const formatDateForInput = (dateStr?: string | null): string => {
        if (!dateStr) {
            return '';
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
            return '';
        }
        return d.toISOString().split('T')[0];
    };

    const formatDateForDisplay = (dateStr?: string | null): string => {
        if (!dateStr) {
            return '—';
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
            return '—';
        }
        return d.toLocaleDateString('en-US');
    };

    const clerksColumns: ITableColumn[] = [
        { name: 'State', mapping: 'state' },
        { name: 'County', mapping: 'county' },
        { name: 'City', mapping: 'city' },
        { name: 'Address', mapping: 'addr' },
        { name: 'Zip Code', mapping: 'zip' },
        { name: 'Phone', mapping: 'phone' },
    ];

    // --- FIX: Dynamic rowCount for the Paginated Clerks Data ---
    const clerksTableConfig: ITableConfig = {
        rowCount: clerksData && clerksData.items ? clerksData.items.length : 0,
        pageNumber: pageNumber,
        idField: 'id',
        columns: clerksColumns,
    };

    const notaryColumns: ITableColumn[] = [
        { name: 'Name', mapping: 'name1' },
        { name: 'Commission Date', mapping: 'commissionDate', format: formatDateForDisplay },
        { name: 'Phone', mapping: 'phone' },
    ];

    const filteredNotaries = useMemo(() => {
        return notaries.filter((n) => (n.name1 || '').toLowerCase().includes(notarySearchQuery.toLowerCase()));
    }, [notaries, notarySearchQuery]);

    // Dynamic rowCount for Notary Data
    const notaryTableConfig: ITableConfig = {
        rowCount: filteredNotaries.length,
        pageNumber: 1,
        idField: 'id',
        columns: notaryColumns,
    };

    const notaryPagedData = new PagedResultSet<INotary>();
    notaryPagedData.items = filteredNotaries.map((n) => ({ ...n, id: n.name1 }));
    notaryPagedData.totalRecords = filteredNotaries.length;
    notaryPagedData.pageNumber = 1;
    notaryPagedData.pageSize = filteredNotaries.length || 10;

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
                <style>{`
                    .modal-table-container {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        border: none !important;
                    }
                    .modal-table-container table {
                        width: 100%;
                        border-collapse: collapse;
                        text-align: left;
                        margin-bottom: 0 !important;
                        border: none !important;
                        box-shadow: none !important;
                    }
                    .modal-table-container th, 
                    .modal-table-container td {
                        border-left: none !important;
                        border-right: none !important;
                    }
                    .modal-table-container th {
                        background-color: #FFFFFF;
                        color: #6B7280;
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        padding: 0.75rem 1rem;
                        border-bottom: 2px solid #E5E7EB !important;
                        border-top: none !important;
                        white-space: nowrap;
                        position: sticky;
                        top: 0;
                        z-index: 10;
                    }
                    .modal-table-container td {
                        padding: 0.75rem 1rem;
                        font-size: 0.875rem;
                        color: #111827;
                        border-bottom: 1px solid #F3F4F6 !important;
                        white-space: nowrap;
                    }
                    
                    .modal-table-container tbody tr:last-child td {
                        border-bottom: none !important;
                    }

                    .modal-table-container tbody tr {
                        cursor: pointer;
                        transition: background-color 0.15s ease;
                    }
                    .modal-table-container tbody tr:hover td {
                        background-color: #F9FAFB !important;
                    }

                    .modal-table-container::-webkit-scrollbar {
                        width: 8px;
                    }
                    .modal-table-container::-webkit-scrollbar-track {
                        background: transparent;
                        margin-top: 40px; 
                    }
                    .modal-table-container::-webkit-scrollbar-thumb {
                        background-color: #D1D5DB;
                        border-radius: 10px;
                    }
                    .modal-table-container::-webkit-scrollbar-thumb:hover {
                        background-color: #9CA3AF;
                    }

                    .pager-sticky-wrapper {
                        position: sticky !important;
                        bottom: 0 !important;
                        z-index: 20 !important;
                        background-color: #FFFFFF !important;
                        border-top: 1px solid #E5E7EB !important;
                        padding: 1rem 0 0 0 !important;
                        margin: auto 0 0 0 !important;
                        flex-shrink: 0;
                    }
                `}</style>

                <div style={styles.modalHeader}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <button
                            onClick={() => {
                                setActiveTab('clerks');
                                resetToSearch();
                            }}
                            style={activeTab === 'clerks' ? styles.tabActive : styles.tabInactive}
                        >
                            County Clerks
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('notary');
                                resetToSearch();
                            }}
                            style={activeTab === 'notary' ? styles.tabActive : styles.tabInactive}
                        >
                            Notary Information
                        </button>
                    </div>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                </div>

                <div style={styles.modalBody}>
                    {/* --- LIST VIEW --- */}
                    {viewMode === 'list' && (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1.5rem', overflow: 'hidden' }}>
                            {/* Toolbar (Fixed at Top) */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                {activeTab === 'clerks' ? (
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <select
                                            style={styles.searchInput}
                                            value={stateFilter}
                                            onChange={(e) => {
                                                setStateFilter(e.target.value);
                                                setCountyFilter('');
                                            }}
                                        >
                                            <option value="">All States...</option>
                                            {statesList.map((s) => (
                                                <option key={`filter-state-${s.value}`} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            style={{
                                                ...styles.searchInput,
                                                backgroundColor: !stateFilter ? '#F9FAFB' : '#FFFFFF',
                                                cursor: !stateFilter ? 'not-allowed' : 'pointer',
                                                color: !stateFilter ? '#9CA3AF' : '#111827',
                                            }}
                                            value={countyFilter}
                                            onChange={(e) => setCountyFilter(e.target.value)}
                                            disabled={!stateFilter}
                                        >
                                            <option value="">{stateFilter ? 'All Counties...' : 'Select State First'}</option>
                                            {filterCountiesList.map((c) => (
                                                <option key={`filter-county-${c.value}`} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>

                                        <LeaseRecordInput
                                            style={{ ...styles.searchInput, width: '200px' }}
                                            placeholder="Search Address/City..."
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <button onClick={handleClerkSearch} style={styles.buttonPrimary}>
                                            Search
                                        </button>
                                        {(stateFilter || countyFilter || searchFilter) && (
                                            <button onClick={handleClerkClearFilters} style={styles.buttonSecondary}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <LeaseRecordInput
                                            style={styles.searchInput}
                                            placeholder="Search by Name..."
                                            value={notarySearchQuery}
                                            onChange={(e) => setNotarySearchQuery(e.target.value)}
                                        />
                                    </div>
                                )}

                                <button onClick={handleAddClick} style={styles.buttonSuccess}>
                                    + Add New Record
                                </button>
                            </div>

                            {/* Tables Container (Scrollable) */}
                            <div className="modal-table-container" style={styles.tableWrapper}>
                                {activeTab === 'clerks' ? (
                                    <Table
                                        isLoading={loading && clerksData === undefined}
                                        tableConfig={clerksTableConfig}
                                        data={clerksData}
                                        onRowClick={handleClerkRowClick}
                                        enablePaging={false}
                                    />
                                ) : (
                                    <Table
                                        isLoading={loading && notaries.length === 0}
                                        tableConfig={notaryTableConfig}
                                        data={notaryPagedData}
                                        onRowClick={handleNotaryRowClick}
                                        enablePaging={false}
                                    />
                                )}
                            </div>

                            {/* Decoupled Pagination Footer (Fixed at Bottom) */}
                            {activeTab === 'clerks' && clerksData && clerksData.totalRecords > 0 && (
                                <div className="pager-sticky-wrapper">
                                    <Pager
                                        pageSize={clerksData.pageSize}
                                        pageNumber={clerksData.pageNumber}
                                        currentPageCount={clerksData.items.length}
                                        totalRecords={clerksData.totalRecords}
                                        onPageChange={handleClerkPageChange}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- FORM VIEW (CLERKS) --- */}
                    {viewMode === 'form' && activeTab === 'clerks' && editingClerk && (
                        <div style={styles.formContainer}>
                            <h3 style={styles.formHeader}>
                                {isCreating ? 'Create New Clerk Record' : `Edit Clerk: ${editingClerk.county}, ${editingClerk.state}`}
                            </h3>
                            <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: '1fr 1fr' }}>
                                <SelectField
                                    label="State (Required)"
                                    value={editingClerk.state}
                                    options={statesList}
                                    editable={isCreating}
                                    onChange={(v) => {
                                        if (editingClerk.state !== v) {
                                            setEditingClerk({ ...editingClerk, state: v || '', county: '' });
                                        }
                                    }}
                                />
                                <SelectField
                                    label="County (Required)"
                                    value={editingClerk.county}
                                    options={editingClerk.state ? countiesList : [{ value: '', label: 'Select State First' }]}
                                    editable={isCreating && editingClerk.state !== ''}
                                    onChange={(v) => setEditingClerk({ ...editingClerk, county: v || '' })}
                                />
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Address"
                                        value={editingClerk.addr || ''}
                                        editable={true}
                                        onChange={(v) => setEditingClerk({ ...editingClerk, addr: v })}
                                    />
                                </div>
                                <Field
                                    label="City"
                                    value={editingClerk.city || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClerk({ ...editingClerk, city: v })}
                                />
                                <Field
                                    label="Zip Code"
                                    value={editingClerk.zip || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClerk({ ...editingClerk, zip: v })}
                                />
                                <Field
                                    label="Phone"
                                    value={editingClerk.phone || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClerk({ ...editingClerk, phone: v })}
                                />
                            </div>
                        </div>
                    )}

                    {/* --- FORM VIEW (NOTARIES) --- */}
                    {viewMode === 'form' && activeTab === 'notary' && editingNotary && (
                        <div style={styles.formContainer}>
                            <h3 style={styles.formHeader}>{isCreating ? 'Create New Notary Record' : `Edit Notary: ${editingNotary.name1}`}</h3>
                            <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: '1fr' }}>
                                <Field
                                    label="Name (Required)"
                                    value={editingNotary.name1}
                                    editable={isCreating}
                                    onChange={(v) => setEditingNotary({ ...editingNotary, name1: v })}
                                />
                                <Field
                                    label="Commission Date"
                                    value={formatDateForInput(editingNotary.commissionDate)}
                                    type="date"
                                    editable={true}
                                    onChange={(v) => setEditingNotary({ ...editingNotary, commissionDate: v })}
                                />
                                <Field
                                    label="Phone"
                                    value={editingNotary.phone || ''}
                                    editable={true}
                                    onChange={(v) => setEditingNotary({ ...editingNotary, phone: v })}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    {viewMode === 'form' ? (
                        <>
                            {!isCreating && (
                                <button onClick={handleDelete} style={styles.buttonDanger}>
                                    Delete Record
                                </button>
                            )}
                            <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
                                <button onClick={resetToSearch} style={styles.buttonSecondary}>
                                    Cancel
                                </button>
                                <button onClick={handleSave} style={styles.buttonSuccess} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Record'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <button onClick={onClose} style={{ ...styles.buttonSecondary, marginLeft: 'auto' }}>
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '1200px',
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        padding: '1.5rem 1.5rem 0',
        borderBottom: '1px solid #E5E7EB',
    },
    tabActive: {
        background: 'none',
        border: 'none',
        outline: 'none',
        borderBottom: '3px solid #4F46E5',
        color: '#111827',
        fontSize: '1.125rem',
        fontWeight: 600,
        paddingBottom: '0.75rem',
        cursor: 'pointer',
    },
    tabInactive: {
        background: 'none',
        border: 'none',
        outline: 'none',
        borderBottom: '3px solid transparent',
        color: '#6B7280',
        fontSize: '1.125rem',
        fontWeight: 500,
        paddingBottom: '0.75rem',
        cursor: 'pointer',
        transition: 'color 0.2s',
    },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        marginBottom: '0.5rem',
    },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    searchInput: {
        padding: '0.5rem 1rem',
        width: '150px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '0.875rem',
        outline: 'none',
        backgroundColor: '#FFFFFF',
        color: '#111827',
    },
    tableWrapper: {
        overflowY: 'auto',
        flexGrow: 1,
        backgroundColor: 'white',
        position: 'relative',
    },
    formContainer: { maxWidth: '800px', margin: '0 auto', width: '100%', padding: '1rem' },
    formHeader: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalFooter: {
        padding: '1rem 1.5rem',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderBottomLeftRadius: '1rem',
        borderBottomRightRadius: '1rem',
    },
    buttonPrimary: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSecondary: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSuccess: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#10B981',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonDanger: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#DC2626',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
};

export default ClerksNotaryModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ClerksNotaryModal.tsx